import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch } from "../store";
import { studentFeeLedgerActions as slice } from "../slices/studentFeeLedger";
import { getErrorMessage } from "../../utils/errorUtils";

import { StudentFeeLedgerRequestDto } from "../../shared/dtos/studentFeeLedger/StudentFeeLedgerRequestDto";
import { StudentFeeLedgerResponseDto } from "../../shared/dtos/studentFeeLedger/StudentFeeLedgerResponseDto";
import { FeeStatementDto } from "../../shared/dtos/studentFeeLedger/FeeStatementDto";
import { BulkPaymentUploadResponseDto } from "../../shared/dtos/studentFeeLedger/BulkPaymentUploadResponseDto";
import { FeeReminderRequestDto } from "../../shared/dtos/studentFeeLedger/FeeReminderRequestDto";
import { EmailRequestDto } from "../../shared/dtos/common/EmailRequestDto";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { PaginatedResponse } from "../../shared/dtos/common/PaginatedResponse";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";
import { TransactionType } from "../../shared/enums/TransactionType";
import { PaymentMethod } from "../../shared/enums/PaymentMethod";

export interface StudentFeeLedgerQueryParams {
  branchId?: string;
  studentId?: string;
  termId?: string;
  type?: TransactionType;
  paymentMethod?: PaymentMethod;
  voteHeadId?: string;
  search?: string;
  page?: number;
  size?: number;
}

export const getStudentStatement =
  (params: { studentId: string; termId: string }) =>
  async (dispatch: AppDispatch) => {
    dispatch(slice.setLoadingStatement());
    try {
      const { data } = await axiosInstance.get<ApiResponseDto<FeeStatementDto>>(
        `/student-fee-ledgers/statement/${params.studentId}`,
        {
          headers: { requiresAuth: true },
          params: { termId: params.termId },
        },
      );

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(slice.setStudentStatement(data.data));
      }
    } catch (error) {
      dispatch(slice.setErrorLoadingStatement(getErrorMessage(error)));
    }
  };

export const getLedgerEntries =
  (params: StudentFeeLedgerQueryParams = { page: 1, size: 10 }) =>
  async (dispatch: AppDispatch) => {
    dispatch(slice.setLoadingLedgerEntries());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<PaginatedResponse<StudentFeeLedgerResponseDto>>
      >("/student-fee-ledgers", {
        headers: { requiresAuth: true },
        params,
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(slice.setLedgerEntries(data.data.content));
        dispatch(slice.setPagination(data.data.pagination));
      }
    } catch (error) {
      dispatch(slice.setErrorLoadingLedgerEntries(getErrorMessage(error)));
    }
  };

export const recordTransaction =
  (values: StudentFeeLedgerRequestDto) => async (dispatch: AppDispatch) => {
    dispatch(slice.setLoadingTransactionCreate());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<StudentFeeLedgerResponseDto>
      >("/student-fee-ledgers", values, { headers: { requiresAuth: true } });

      if (
        data.status === ApiResponseStatus.SUCCESS ||
        data.statusCode === 201
      ) {
        toast.success(data.message || "Transaction recorded successfully!");
        dispatch(slice.createTransactionSuccess());
        dispatch(
          slice.setServerResponseStatus({
            operation: "create",
            status: data.statusCode || 201,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(slice.setErrorCreatingTransaction(message));
    }
  };

export const generateTermFees =
  (params: { termId: string; branchId: string }) =>
  async (dispatch: AppDispatch) => {
    dispatch(slice.setLoadingBatchGeneration());
    try {
      const { data } = await axiosInstance.post<ApiResponseDto<string>>(
        "/student-fee-ledgers/generate-batch",
        null,
        {
          headers: { requiresAuth: true },
          params,
        },
      );

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message);
        dispatch(slice.setBatchGenerationSuccess(data.data));
        dispatch(
          slice.setServerResponseStatus({
            operation: "batch_generate",
            status: 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(slice.setErrorBatchGeneration(message));
    }
  };

export const sendFeeReminders =
  (payload: FeeReminderRequestDto) => async (dispatch: AppDispatch) => {
    dispatch(slice.setLoadingReminders());
    try {
      const { data } = await axiosInstance.post<ApiResponseDto<string>>(
        "/student-fee-ledgers/send-reminders",
        payload,
        { headers: { requiresAuth: true } },
      );

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Reminders queued successfully.");
        dispatch(slice.setRemindersSuccess(data.data));
        dispatch(
          slice.setServerResponseStatus({
            operation: "reminders",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(slice.setErrorReminders(message));
    }
  };

export const uploadBulkPayments =
  (payload: { file: File; termId: string }) =>
  async (dispatch: AppDispatch) => {
    dispatch(slice.setLoadingUpload());
    try {
      const formData = new FormData();
      formData.append("file", payload.file);
      formData.append("termId", payload.termId);

      const { data } = await axiosInstance.post<
        ApiResponseDto<BulkPaymentUploadResponseDto>
      >("/student-fee-ledgers/upload-payments", formData, {
        headers: {
          requiresAuth: true,
          "Content-Type": "multipart/form-data",
        },
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        const result = data.data;
        if (result.failedImports > 0) {
          toast.warning(`Upload complete with ${result.failedImports} errors.`);
        } else {
          toast.success(data.message || "All payments imported successfully!");
        }
        dispatch(slice.setUploadSuccess(result));
        dispatch(
          slice.setServerResponseStatus({ operation: "upload", status: 200 }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(slice.setErrorUpload(message));
    }
  };

export const getFeeStatementPdf =
  (params: { studentId: string; termId: string }) =>
  async (dispatch: AppDispatch) => {
    dispatch(slice.setLoadingPdf());
    try {
      const { data } = await axiosInstance.get(
        `/student-fee-ledgers/statement/pdf/${params.studentId}`,
        {
          headers: { requiresAuth: true },
          params: { termId: params.termId },
          responseType: "blob",
        },
      );

      const pdfBlobUrl = window.URL.createObjectURL(
        new Blob([data], { type: "application/pdf" }),
      );

      dispatch(slice.setPdfUrl(pdfBlobUrl));
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message || "Failed to download PDF statement.");
      dispatch(slice.setErrorPdf(message));
    }
  };

export const emailFeeStatement =
  (
    params: { studentId: string; termId: string },
    emailPayload: EmailRequestDto,
  ) =>
  async (dispatch: AppDispatch) => {
    dispatch(slice.setLoadingEmail());
    try {
      const { data } = await axiosInstance.post<ApiResponseDto<void>>(
        `/student-fee-ledgers/statement/email/${params.studentId}`,
        emailPayload,
        {
          headers: { requiresAuth: true },
          params: { termId: params.termId },
        },
      );

      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success(data.message || "Fee statement emailed successfully!");
        dispatch(slice.setEmailSuccess());
        return true;
      }
      return false;
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(slice.setErrorEmail(message));
      return false;
    }
  };

export const resetFinanceServerStatus = () => (dispatch: AppDispatch) => {
  dispatch(slice.setServerResponseStatus(null));
};

export const resetFinanceStateAction = () => (dispatch: AppDispatch) => {
  dispatch(slice.resetFinanceState());
};
